import React, { useState, useEffect } from 'react';
import { ServiceConfig } from '../../utils/serviceKeys';
import { type User } from '../../services/graphql/authService';
import { contactService } from '../../services/graphql/bookingService';
import { useServiceKey } from '../../hooks/useServiceKey';
import QrCodePanel from '../QrCodePanel';
import { trackPageView, trackFormInteraction, trackEvent, trackError } from '../../services/analytics';
import InputField from '../ui/InputField';
import Textarea from '../ui/Textarea';

// Dynamically generate the app link based on the website root and service key
const getAppLink = (serviceKey: string | null, currentUser: User | null) => {
  const root = typeof window !== 'undefined' ? window.location.origin : '';
  let link = serviceKey && serviceKey !== 'default' ? `${root}/${serviceKey}` : root;
  if (currentUser?.$id) {
    link += `?source=${currentUser.$id}`;
  }
  return link;
};

interface AboutProps {
  serviceConfig?: ServiceConfig;
  currentUser?: User | null;
}

const About: React.FC<AboutProps> = ({ serviceConfig, currentUser }) => {
  const { serviceKey } = useServiceKey();
  const serviceName = serviceConfig?.serviceName || 'mythos365';
  const appLink = getAppLink(serviceKey, currentUser || null);
  const primaryColor = (serviceConfig as any)?.colors?.primary || serviceConfig?.primaryColor || '#007bff';

  // Contact form state
  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [contactSuccess, setContactSuccess] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactLoading, setContactLoading] = useState(false);

  // Feedback form state
  const [feedback, setFeedback] = useState({ rating: 0, feedbackText: '' });
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Initialize form with user data if available and when currentUser changes
  React.useEffect(() => {
    console.log('[About] currentUser:', currentUser);
    if (currentUser) {
      // Fill contact form with user data if available
      setContact(prevContact => ({
        ...prevContact,
        name: currentUser.name || currentUser.prefs?.full_name || currentUser.prefs?.name || '',
        email: currentUser.email || '',
      }));
    } else {
      setContact(prevContact => ({
        ...prevContact,
        name: '',
        email: '',
      }));
    }
  }, [currentUser]);

  // Track page view when component mounts
  useEffect(() => {
    trackPageView(`/${serviceKey}/about`);
  }, [serviceKey]);

  // Handlers for contact form
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fieldName = e.target.name;
    setContact({ ...contact, [fieldName]: e.target.value });

    // Track form field interactions
    trackFormInteraction('ContactForm', 'FieldChange', fieldName);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[About] handleContactSubmit - currentUser:', currentUser);
    console.log('[About] handleContactSubmit - contact state:', contact);

    // Track form submission attempt
    trackFormInteraction('ContactForm', 'SubmitAttempt');

    if (!contact.email || !contact.message || !contact.name) {
      setContactError('Please fill in all required fields (Name, Email, Message).');
      trackFormInteraction('ContactForm', 'ValidationFailed');
      return;
    }

    setContactLoading(true);
    setContactError(null);
    setContactSuccess(null);

    try {
      const userIdToSend = currentUser?.$id || null;
      console.log('[About] handleContactSubmit - user_id to send:', userIdToSend);

      await contactService.submitContactForm({
        name: contact.name,
        email: contact.email,
        message: contact.message,
        user_id: userIdToSend,
        service_key: serviceKey,
      });

      // Track successful submission
      trackFormInteraction('ContactForm', 'SubmitSuccess');
      trackEvent('Contact', 'Message Sent', serviceKey);

      setContactSuccess('Your message has been sent successfully!');
      setContact(prevContact => ({
        ...prevContact,
        message: '',
        name: currentUser ? (currentUser.name || '') : '',
        email: currentUser ? (currentUser.email || '') : '',
      }));
    } catch (error: any) {
      // Track submission failure
      trackFormInteraction('ContactForm', 'SubmitFailed');
      trackError(`Contact form submission error: ${error.message || 'Unknown error'}`, false);

      setContactError(error.message || 'Failed to send message.');
    } finally {
      setContactLoading(false);
    }
  };

  // Handlers for feedback form
  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFeedback(prev => ({ ...prev, [name]: name === 'rating' ? parseInt(value, 10) : value }));

    // Track feedback form field changes
    trackFormInteraction('FeedbackForm', 'FieldChange', name);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[About] handleFeedbackSubmit - currentUser:', currentUser);
    console.log('[About] handleFeedbackSubmit - feedback state:', feedback);

    // Track feedback submission attempt
    trackFormInteraction('FeedbackForm', 'SubmitAttempt');
    trackEvent('Feedback', 'Submission Attempt', serviceKey);

    if (feedback.rating === 0 || !feedback.feedbackText) {
      setFeedbackError('Please provide a rating and your feedback message.');
      trackFormInteraction('FeedbackForm', 'ValidationFailed');
      return;
    }

    setFeedbackLoading(true);
    setFeedbackError(null);
    setFeedbackSuccess(null);

    try {
      const userIdToSend = currentUser?.$id || null;
      console.log('[About] handleFeedbackSubmit - user_id to send:', userIdToSend);

      await contactService.submitFeedback({
        rating: feedback.rating,
        feedback: feedback.feedbackText,
        user_id: userIdToSend,
        service_key: serviceKey,
      });

      // Track successful feedback submission with rating
      trackFormInteraction('FeedbackForm', 'SubmitSuccess');
      trackEvent('Feedback', 'Submitted', serviceKey, feedback.rating);

      setFeedbackSuccess('Thank you for your feedback!');
      setFeedback({ rating: 0, feedbackText: '' }); // Reset form
    } catch (error: any) {
      // Track submission failure
      trackFormInteraction('FeedbackForm', 'SubmitFailed');
      trackError(`Feedback form submission error: ${error.message || 'Unknown error'}`, false);

      setFeedbackError(error.message || 'Failed to submit feedback.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <div className="card-container mx-auto px-4 py-8 text-foreground">
      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-2">About {serviceName}</h1>
        <p className="text-lg mb-4">{serviceName} is your trusted partner for on-demand and scheduled rides, serving individuals and businesses with reliability and care.</p>

        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div>
            {/* QR Code Section */}
            <QrCodePanel appLink={appLink} primaryColor={primaryColor} />
            <p className="text-xs mt-2 text-center">Scan to open our app</p>
          </div>
          <div>            <a
              href={appLink}
              className="text-primary underline hover:text-primary/80"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open {serviceName} App
            </a>
            {serviceConfig?.contactInfo?.phone && (
              <p className="text-sm mt-2">
                <strong>Phone:</strong> {serviceConfig.contactInfo.phone}
              </p>
            )}
            {serviceConfig?.contactInfo?.email && (
              <p className="text-sm mt-1">
                <strong>Email:</strong> {serviceConfig.contactInfo.email}
              </p>
            )}
          </div>
        </div>
      </section>      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-2">Contact Us</h2>
        <form onSubmit={handleContactSubmit} className="space-y-3" aria-label="Contact form">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium">Name</label>
            <InputField
              id="contact-name"
              name="name"
              type="text"
              value={contact.name}
              onChange={handleContactChange}
              required
              inputClassName="form-input mt-1"
              autoComplete="name"
              readOnly={!!currentUser}
              placeholder="Your Name"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium">Email</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              value={contact.email}
              onChange={handleContactChange}
              required
              className="form-input mt-1"
              autoComplete="email"
              readOnly={!!currentUser}
            />
            {currentUser && (
              <small className="text-foreground-muted">Using your account email</small>
            )}
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium">Message</label>
            <Textarea id="contact-message" name="message" value={contact.message} onChange={handleContactChange} required className="form-input mt-1" rows={3} />
          </div>          <button
            type="submit"
            className="px-4 py-2 rounded disabled:opacity-60 bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={contactLoading}
          >
            {contactLoading ? 'Sending...' : 'Send Message'}
          </button>
          {contactSuccess && <p className="text-success text-sm">{contactSuccess}</p>}
          {contactError && <p className="text-destructive text-sm">{contactError}</p>}
        </form>
      </section>      <section>
        <h2 className="text-xl font-semibold mb-2">Feedback (Optional)</h2>
        <form onSubmit={handleFeedbackSubmit} className="max-w-lg mx-auto bg-background-paper p-6 md:p-8 rounded-lg shadow-lg">
          <div className="mb-4">
            <label htmlFor="rating" className="block text-sm font-medium text-foreground-muted mb-1">Overall Rating</label>
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button"
                  key={star}
                  onClick={() => setFeedback(prev => ({ ...prev, rating: star }))}                  className={`text-2xl focus:outline-none ${feedback.rating >= star ? 'text-primary' : 'text-muted'}`}
                  aria-label={`Rate ${star} out of 5 stars`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <label htmlFor="feedbackText" className="block text-sm font-medium text-foreground-muted mb-1">Your Feedback</label>
            <Textarea
              id="feedbackText"
              name="feedbackText"
              rows={4}
              className="form-input"
              placeholder="Tell us about your experience..."
              value={feedback.feedbackText}
              onChange={handleFeedbackChange}
              required
            />
          </div>          <button
            type="submit"
            disabled={feedbackLoading}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-150 flex items-center justify-center hover:bg-primary/90"
          >
            {feedbackLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>          {feedbackSuccess && <p className="mt-3 text-sm text-success text-center">{feedbackSuccess}</p>}
          {feedbackError && <p className="mt-3 text-sm text-destructive text-center">{feedbackError}</p>}
        </form>
      </section>
    </div>
  );
};

export default About;
